/* eslint-disable import/no-cycle */
import { defineStore } from 'pinia';
import Parse, { PARSE_SERVER_URL } from '@/store/parseInitialization';
import { usePatientStore } from '@/store/patient';
import { useMedicineStore } from '@/store/medicine';
import { useAppStore } from '@/store/app';
import { cleanFileUrl } from '@/script/index';
import { queryClient } from '@/plugins/query';
import {
  getCachedIsDoctor,
  setCachedIsDoctor,
  getCachedAuthStatus,
  setCachedAuthStatus,
  invalidateIsDoctor,
} from '@/plugins/storage/isDoctorCache';

export const defaultDoctorPhoto = 'https://api.dicebear.com/8.x/fun-emoji/svg?seed=Pepper';

export const useDoctorStore = defineStore('doctor', {
  state: () => ({
    app: useAppStore(),
    patient: usePatientStore(),
    medicine: useMedicineStore(),
    user: {
      id: '',
      email: '',
      photo: defaultDoctorPhoto,
      firstname: '',
      lastname: '',
      phone: '',
      gender: 'other',
      address: '',
      updatedAt: null,
      createdAt: null,
    },
    isLoggedIn: false,
    rememberMe: false,
    // Persisted: true if the doctor also holds the admin role. Surfaced
    // by `getAuthStatus` on every session refresh; used by router guards
    // and the AppBar to show admin-only views (/doctors, etc.).
    isAdmin: false,
  }),
  persist: {
    key: 'doctor',
    storage: localStorage,
    paths: ['user', 'isLoggedIn', 'rememberMe', 'isAdmin'],
  },
  actions: {
    async login(email, password, rememberMe) {
      // Legacy path. The comment here used to say it was "kept for Register's
      // auto-login flow"; that is no longer true - Register calls
      // `doctor.register`, and nothing in src/ calls this action or `this.login`.
      // It is currently unreachable. Kept rather than deleted because the
      // migration it belongs to is not finished, but treat it as dead code.
      //
      // New code uses useLoginMutation + the setUserFromLoginResponse setter
      // below; that path also handles error semantics distinctly (invalid /
      // unverified / throttled).
      try {
        const response = await Parse.Cloud.run('loginDoctor', { email, password });

        // update current user data to be logged in
        await Parse.User.enableUnsafeCurrentUser(); // Ensure the current user can be accessed
        await Parse.User.become(response.sessionToken);

        // Same cross-account guard as useLoginMutation's onSuccess. Without it,
        // reviving this path would silently reintroduce exactly the cache bleed
        // this commit exists to close: query keys are not scoped per doctor, so
        // anything still cached would paint as the incoming doctor's data.
        queryClient.clear();
        this.setUserFromLoginResponse(response, rememberMe);
        invalidateIsDoctor();
      } catch (error) {
        this.app.raiseAlert(error.message);
      }
    },
    setUserFromLoginResponse(response, rememberMe) {
      // Pure state setter - no network. Called by useLoginMutation's
      // onSuccess after the cloud-function call has resolved and the
      // SDK current-user has been hydrated.
      this.user.id = response.objectId;
      this.user.email = response.email;
      this.user.photo = cleanFileUrl(response.photo, PARSE_SERVER_URL) ?? this.user.photo;
      this.user.firstname = response.firstname;
      this.user.lastname = response.lastname;
      this.user.updatedAt = response.updatedAt;
      this.user.createdAt = response.createdAt;
      this.isLoggedIn = true;
      this.rememberMe = rememberMe;
    },
    async resetPassword(email) {
      try {
        // Custom Mailgun-backed cloud function - see comment in
        // useResetPasswordMutation. Parse SDK's built-in passwordReset
        // silently no-ops because no emailAdapter is configured.
        await Parse.Cloud.run('requestPasswordReset', { email });

        this.app.raiseAlert(' Reset email send successfully', 'success');
      } catch (error) {
        this.app.raiseAlert(error.message);
      }
    },
    async logout() {
      try {
        const currentUser = Parse.User.current();
        if (currentUser) {
          const sessionToken = currentUser.getSessionToken();
          // logout
          await Parse.User.logOut(sessionToken);
        }
      } catch (error) {
        // Ending the server-side session failed - offline, or the token had
        // already expired. Worth reporting, but it must never skip the local
        // wipe below: that wipe is the only thing stopping the next doctor on
        // this machine from seeing this doctor's patients.
        this.app.raiseAlert(error.message);
      } finally {
        invalidateIsDoctor();

        // Drop every cached cloud-function result. Must run BEFORE the
        // localStorage.clear() below: the persister subscribes to the live
        // cache and rewrites it on a 1s throttle, so clearing storage while
        // the in-memory cache still holds this doctor's data just puts that
        // data straight back on disk.
        queryClient.clear();

        // reset all pinia's states
        this.$reset(); // reset doctor state
        this.patient.$reset(); // reset patient state
        this.medicine.$reset(); // reset medicine state

        // clear all data from localstorage
        localStorage.clear();
        sessionStorage.clear();
      }
    },
    async register(firstname, lastname, email, password, isPrivacyConfirmed) {
      try {
        const { message, code } = await Parse.Cloud.run(
          'signupDoctor',
          {
            firstname,
            lastname,
            email,
            password,
            isPrivacyConfirmed,
          },
        );

        // Keep email to prefill the login form; never persist the password.
        this.user.email = email;

        // show message that doctor saved succefully
        if (message && code === 201) {
          this.app.raiseAlert(message, 'success');
        }
        return true;
      } catch (error) {
        this.app.raiseAlert(error.message);
        return false;
      }
    },
    async getProfile() {
      try {
        const response = await Parse.Cloud.run('getProfile');
        this.user.photo = cleanFileUrl(response.photo, PARSE_SERVER_URL) ?? this.user.photo;
        this.user.email = response.email;
        this.user.firstname = response.firstname ?? '';
        this.user.lastname = response.lastname ?? '';
        this.user.gender = response.gender ?? 'other';
        this.user.phone = response.phone ?? '';
        this.user.address = response.address ?? '';
      } catch (error) {
        throw new Error(error.message);
      }
    },
    async updateProfile(doctor) {
      try {
        const response = await Parse.Cloud.run('updateProfile', doctor);
        this.user.photo = cleanFileUrl(response.photo, PARSE_SERVER_URL) ?? this.user.photo;
        this.user.email = response.email;
        this.user.firstname = response.firstname ?? '';
        this.user.lastname = response.lastname ?? '';
        this.user.gender = response.gender ?? 'other';
        this.user.phone = response.phone ?? '';
        this.user.address = response.address ?? '';
        // show message that doctor saved succefully
        if (response) {
          this.app.raiseAlert(`Update patient ${this.user.firstname} succefully`, 'success');
        }
      } catch (error) {
        throw new Error(error.message);
      }
    },
    async addPatientByEmail(email) {
      try {
        return await Parse.Cloud.run('addPatientByEmail', { email });
      } catch (error) {
        throw new Error(error.message);
      }
    },
    // to see
    async signupPatientByDoctor(
      email,
      firstname,
      lastname,
      gender,
      birthday,
      birthplace,
      phone,
      height,
      heightUnity,
      weight,
      weightUnity,
      edss,
      notes,
      injectables,
      orals,
      infusions,
    ) {
      try {
        return await Parse.Cloud.run(
          'signupPatientByDoctor',
          {
            email,
            firstname,
            lastname,
            gender,
            birthday: birthday.toISOString(),
            birthplace,
            phone,
            height,
            heightUnity,
            weight,
            weightUnity,
            edss,
            notes,
            medicines: [
              ...injectables,
              ...orals,
              ...infusions,
            ],
          },
        );
      } catch (error) {
        throw new Error(error.message);
      }
    },
    async isDoctorLoggedIn() {
      const cached = getCachedIsDoctor();
      if (cached !== null) return cached;
      try {
        const result = await Parse.Cloud.run('isDoctor');
        setCachedIsDoctor(result);
        return result;
      } catch (error) {
        return false;
      }
    },
    // Resolve the richer onboarding state used by the router guard. An
    // approved doctor is `approved: true`; one still on the waiting list
    // is `authenticated: true, approved: false`. On any failure we fall
    // back to a logged-out shape so the guard sends the user to /login.
    async getAuthStatus() {
      const cached = getCachedAuthStatus();
      if (cached !== null) {
        // Keep the persisted isAdmin flag in sync with the cache for
        // reactive consumers (AppBar nav, route guards).
        this.isAdmin = Boolean(cached.isAdmin);
        return cached;
      }
      try {
        const status = await Parse.Cloud.run('getAuthStatus');
        setCachedAuthStatus(status);
        this.isAdmin = Boolean(status.isAdmin);
        return status;
      } catch (error) {
        // `getAuthStatus` is a new Cloud Function; an older/not-yet-deployed
        // backend won't have it. Fall back to the long-standing `isDoctor`
        // gate so an approved doctor is never locked out, and we never make
        // the router depend on a function the server might not expose.
        // (A waiting-list doctor can't hold a session - loginDoctor blocks
        // them - so isDoctor === approved in practice.)
        try {
          const isDoctor = await Parse.Cloud.run('isDoctor');
          const status = {
            authenticated: isDoctor,
            role: isDoctor ? 'doctor' : null,
            approved: isDoctor,
            isAdmin: false,
          };
          setCachedAuthStatus(status);
          this.isAdmin = false;
          return status;
        } catch {
          this.isAdmin = false;
          return {
            authenticated: false, role: null, approved: false, isAdmin: false,
          };
        }
      }
    },
    async updateProfilePhoto(imageName, imageFile) {
      try {
        // convert img to base64
        const response = await Parse.Cloud.run('addOrUpdatePhoto', { imageName, imageFile });
        // show message that doctor updated photo succefully
        if (response.success) {
          this.user.photo = cleanFileUrl(response.photo, PARSE_SERVER_URL) ?? this.user.photo;
          this.app.raiseAlert('Update profile picture succefully', 'success');
        }
      } catch (error) {
        this.app.raiseAlert(`${error}`, 'error');
      }
    },
    async deleteProfilePhoto() {
      try {
        const response = await Parse.Cloud.run('deletePhoto');
        this.user.photo = cleanFileUrl(response.photo, PARSE_SERVER_URL) ?? this.user.photo;
        this.user.email = response.email;
        this.user.firstname = response.firstname ?? '';
        this.user.lastname = response.lastname ?? '';
        this.user.gender = response.gender ?? 'other';
        this.user.phone = response.phone ?? '';
        this.user.address = response.address ?? '';
        // show message that doctor deleted photo succefully
        if (response.success) {
          this.app.raiseAlert('Delete profile picture succefully', 'success');
        }
      } catch (error) {
        this.app.raiseAlert(`${error}`, 'error');
      }
    },
  },
});
